import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

export interface FreshservicePreviewItem {
  ticket_id: number;
  subject: string;
  description_text: string;
  requester_email: string | null;
  requester_name: string | null;
  priority: number;
  priority_label: string;
  status_label: string;
  created_at: string | null;
  url: string;
  already_imported: boolean;
  imported_as: string | null;
  agent_id?: number | null;
  agent_name?: string | null;
}

export interface FreshserviceImportItem {
  ticket_id: number;
  project_id: string | null;
  error: string | null;
}

export interface FreshserviceImportResponse {
  imported: FreshserviceImportItem[];
  skipped: FreshserviceImportItem[];
}

export interface FreshserviceStatus {
  configured: boolean;
  subdomain: string;
}

export interface ServiceDeskRequester {
  name: string | null;
  email: string | null;
  job_title: string | null;
}

export interface ServiceDeskAttachment {
  id: number;
  name: string;
  content_type: string | null;
  size: number | null;      // bytes
  url: string;              // signed Freshservice URL
  is_image: boolean;
}

export interface ServiceDeskConversation {
  id: number;
  author_name: string | null;
  body_text: string;
  body_html: string | null;
  created_at: string | null;
  is_private: boolean;
  source_label: string;
  attachments: ServiceDeskAttachment[];
}

export interface ServiceDeskTicketDetail {
  ticket_id: number;
  subject: string;
  description_text: string;
  description_html: string | null;
  priority: number;
  priority_label: string;
  status_label: string;
  created_at: string | null;
  updated_at: string | null;
  url: string;
  requester: ServiceDeskRequester | null;
  conversations: ServiceDeskConversation[];
  attachments: ServiceDeskAttachment[];
  already_imported: boolean;
  imported_as: string | null;
}

export interface FreshserviceFilterOptions {
  groups: string[];
  statuses: string[];
}

export function useFreshserviceStatus() {
  return useQuery<FreshserviceStatus>({
    queryKey: ["freshservice", "status"],
    queryFn: () => api.get("/freshservice/status").then((r) => r.data),
    staleTime: 60_000,
  });
}

export function useFreshserviceFilterOptions(enabled: boolean) {
  return useQuery<FreshserviceFilterOptions>({
    queryKey: ["freshservice", "filter-options"],
    queryFn: () => api.get("/freshservice/filter-options").then((r) => r.data),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useFreshserviceTicketDetail(ticketId: number | null) {
  return useQuery<ServiceDeskTicketDetail>({
    queryKey: ["freshservice", "ticket", ticketId],
    queryFn: () =>
      api.get(`/freshservice/ticket/${ticketId}`).then((r) => r.data),
    enabled: ticketId !== null,
    staleTime: 60_000,
    retry: false,
  });
}

/** View filter: 'open' = configured open status (default),
 *  'closed' = configured closed statuses, 'all' = no status filter. */
export type ProjectRequestsView = "open" | "closed" | "all";

/** Group selector: 'primary' = PMO Project Requests, 'ba' = Application Support. */
export type ProjectRequestsGroup = "primary" | "ba";

export function useFreshservicePreview(
  enabled: boolean,
  view: ProjectRequestsView = "open",
  group: ProjectRequestsGroup = "primary",
) {
  return useQuery<FreshservicePreviewItem[]>({
    queryKey: ["freshservice", "preview", group, view],
    queryFn: () =>
      api
        .get("/freshservice/preview", { params: { view, group } })
        .then((r) => r.data),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export interface ProjectFreshserviceAttachment {
  id: number;                  // internal row id, not used in URLs
  project_id: string;
  fs_ticket_id: number;
  fs_attachment_id: number;
  name: string;
  size: number | null;
  content_type: string | null;
  is_image: boolean;
  source_label: string | null;
  created_at_fs: string | null;
  /** Public open URL the backend hands us. Always render this verbatim
   *  rather than constructing a URL from `id` — the URL shape is
   *  backend-owned and is keyed on an opaque per-row token. */
  open_url: string;
  last_synced_at: string;
}

export interface ProjectFreshserviceAttachmentsResponse {
  project_id: string;
  fs_ticket_id: number | null;
  last_synced_at: string | null;
  /** Set when the most recent sync attempt failed. The card should
   *  surface a retry banner instead of misleading the user into
   *  thinking the ticket simply has no attachments. */
  sync_error: string | null;
  attachments: ProjectFreshserviceAttachment[];
}

export function useFreshserviceProjectAttachments(
  projectId: string | null,
  enabled = true,
) {
  return useQuery<ProjectFreshserviceAttachmentsResponse>({
    queryKey: ["freshservice", "project-attachments", projectId],
    queryFn: () =>
      api
        .get(`/freshservice/projects/${projectId}/attachments`)
        .then((r) => r.data),
    enabled: Boolean(projectId) && enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useRefreshFreshserviceProjectAttachments(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ProjectFreshserviceAttachmentsResponse, Error, void>({
    mutationFn: () =>
      api
        .post(`/freshservice/projects/${projectId}/attachments/refresh`)
        .then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(
        ["freshservice", "project-attachments", projectId],
        data,
      );
    },
  });
}

export interface ServiceDeskTicket {
  ticket_id: number;
  subject: string;
  type: string | null;          // 'Incident' | 'Service Request' | ...
  priority: number;             // 1..4
  priority_label: string;       // Low | Medium | High | Highest
  status_label: string;
  group_id: number | null;
  group_name: string | null;
  agent_id: number | null;
  agent_name: string | null;
  created_at: string | null;
  url: string;
}

export interface ServiceDeskOverviewResponse {
  tickets: ServiceDeskTicket[];
  fetched_at: string;
  priority_labels: Record<number, string>;
}

/** Desk-wide unresolved tickets (all groups, all types) for the
 *  Service Desk dashboard. Distinct from useFreshservicePreview, which
 *  is group-scoped + import-oriented. */
export function useServiceDeskOverview(enabled = true) {
  return useQuery<ServiceDeskOverviewResponse>({
    queryKey: ["freshservice", "service-desk"],
    queryFn: () => api.get("/freshservice/service-desk").then((r) => r.data),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useFreshserviceImport() {
  const qc = useQueryClient();
  return useMutation<FreshserviceImportResponse, Error, number[]>({
    mutationFn: (ticketIds) =>
      api
        .post("/freshservice/import", { ticket_ids: ticketIds })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      qc.invalidateQueries({ queryKey: ["freshservice", "preview"] });
    },
  });
}
